import { useQuery } from '@tanstack/react-query';
import type { AccessLogPage, AccessToday, EmailAdmin, EmailAttention, MyInbox } from '@shared/gmail';
import { api } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';

// Queries and small helpers shared by the account section and the admin section.

export const myInboxQuery = {
  queryKey: ['gmail', 'me'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => api.get<MyInbox>('/gmail/me', { signal }),
};

export const emailAdminQuery = {
  queryKey: ['admin', 'email'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => api.get<EmailAdmin>('/gmail/admin', { signal }),
};

export interface LogFilters {
  mailboxId?: string;
  kind?: 'personal' | 'shared' | 'crm';
  action?: string;
}

export type AdminLogPage = AccessLogPage & { today: AccessToday };

/** Local midnight, so "today" in the access log means your today. */
export function startOfToday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function adminLogPage(filters: LogFilters, before: string | null, signal?: AbortSignal): Promise<AdminLogPage> {
  return api.get<AdminLogPage>('/gmail/admin/log', {
    signal,
    query: { ...filters, before: before ?? undefined, since: startOfToday() },
  });
}

export function myLogPage(before: string | null, signal?: AbortSignal): Promise<AccessLogPage> {
  return api.get<AccessLogPage>('/gmail/me/log', { signal, query: { before: before ?? undefined } });
}

/** Drives the dot next to "Email & inboxes" in the admin console. */
export function useEmailAttention(): boolean {
  const { data } = useQuery({
    queryKey: ['gmail', 'attention'],
    queryFn: ({ signal }) => api.get<EmailAttention>('/gmail/attention', { signal }),
    staleTime: 60_000,
  });
  return !!data && data.waiting + data.errors > 0;
}

/** Everything that shows inbox state: refetched after connecting, disconnecting or changing a setting. */
export function refreshInboxes(): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['gmail'] }),
    queryClient.invalidateQueries({ queryKey: ['admin', 'email'] }),
    queryClient.invalidateQueries({ queryKey: ['me'] }),
    queryClient.invalidateQueries({ queryKey: ['team'] }),
  ]).then(() => undefined);
}

/** Where to send the browser to connect an inbox with Google. */
export function connectUrl(kind: 'personal' | 'shared' | 'crm', address?: string): string {
  const q = new URLSearchParams({ purpose: `gmail.${kind}` });
  if (address) q.set('address', address);
  return `/auth/google/start?${q.toString()}`;
}

/** "Maya" from "Maya Lindqvist". */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/** Sends the browser to Google to connect (or reconnect) an inbox. */
export function goConnect(kind: 'personal' | 'shared' | 'crm', address?: string): void {
  window.location.assign(connectUrl(kind, address));
}
